/**
 * Blog listing filter — progressive enhancement for the category tabs and pager.
 *
 * Without JS the tabs / pagination are plain <a> links that reload the whole
 * /blog page (server-side filter — still the no-JS fallback). This intercepts
 * those clicks and swaps only the listing <section> in place via fetch(), so
 * changing category or page never re-downloads the hero logo carousel, fonts,
 * or background images, and doesn't re-render the rest of the page. That's what
 * made switching feel slow.
 *
 *  - history.pushState keeps the URLs shareable and back/forward working.
 *  - Any failure falls back to a normal full navigation, so behaviour degrades
 *    safely.
 *  - Blog card links (.blog__card-link) are deliberately NOT intercepted — they
 *    navigate to the individual post.
 */

const SECTION = 'section.js-blog-listing';
const LINK = '.blog-page-listing__filter, .blog-page-listing__pager, .blog-page-listing__page-btn';

let busy = false;

const fetchSection = async (url) => {
	const res = await fetch(url, {
		headers: { 'X-Requested-With': 'fetch' },
		credentials: 'same-origin',
	});
	if (!res.ok) throw new Error(`HTTP ${res.status}`);
	const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
	const next = doc.querySelector(SECTION);
	if (!next) throw new Error('listing section missing in response');
	return next;
};

const go = async (url, { push }) => {
	const current = document.querySelector(SECTION);
	if (!current || busy) return;

	busy = true;
	current.classList.add('is-loading');

	try {
		const next = await fetchSection(url);
		current.replaceWith(next);
		if (push) history.pushState({ blogListing: true }, '', url);

		// Land keyboard focus on the now-active tab so filtering stays oriented.
		const activeTab = next.querySelector('.blog-page-listing__filter.is-active');
		if (activeTab) activeTab.focus({ preventScroll: true });
	} catch {
		window.location.href = url; // hard fallback — never leave the user stuck
	} finally {
		busy = false;
	}
};

const onClick = (e) => {
	if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
		return;
	}

	const link = e.target.closest(LINK);
	if (!link || link.tagName !== 'A' || !link.getAttribute('href') || !link.closest(SECTION)) {
		return;
	}

	const url = new URL(link.href, window.location.href);
	if (url.origin !== window.location.origin) return; // same-origin only

	e.preventDefault();
	go(url.href, { push: true });
};

const onPopState = () => {
	if (!document.querySelector(SECTION)) return;
	go(window.location.href, { push: false });
};

export const initBlogListingFilter = () => {
	if (!document.querySelector(SECTION)) return;
	if (typeof window.fetch !== 'function' || typeof Element.prototype.replaceWith !== 'function') {
		return; // no-JS-grade fallback: leave the plain links alone
	}

	document.addEventListener('click', onClick);
	window.addEventListener('popstate', onPopState);
};
